// Chooses the content source (Google Sheet if configured, else the bundled demo
// words) and produces (a) the word list the UI renders and (b) the audio
// manifest the audio manager loads.
//
// Audio is a SHARED phonic-sound library keyed by a phoneme CODE (not per-word
// files). Each sound is tagged with the code that selects its file:
//   cell "t"     -> show "t",  play phoneme  t
//   cell "c"     -> show "c",  play phoneme  k   (default map c->k)
//   cell "a"     -> show "a",  play short    a
//   cell "a|ae"  -> show "a",  play long-a   ae  (the |tag disambiguates)
// Files are found by code: <code>.mp3 in the Drive folder / base URL / local
// public/phonics/ folder, with the bundled placeholder as a final fallback.

import { WORDS as BUNDLED } from './words.js'
import { AUDIO_EXT, AUDIO_BASE_URL, PHONEME_BASE, PHONEME_EXT } from './config.js'
import { loadSheetWords } from './sheet.js'
import { loadDriveIndex } from './drive-folder.js'

const bundledPh = (code) => `audio/ph/ph_${code}.${AUDIO_EXT}`
const bundledWord = (id) => `audio/word/word_${id}.${AUDIO_EXT}`
const joinUrl = (base, name) => base.replace(/\/?$/, '/') + name

// Extensions to probe in the local public/phonics/ folder (first match wins).
const LOCAL_EXTS = [...new Set([PHONEME_EXT, 'mp3', 'm4a', 'mp4', 'wav', 'ogg'])]

// Default grapheme -> phoneme code, so common cells need no explicit tag.
const G2P = { c: 'k', x: 'ks', qu: 'kw' }
function codeFor(display, explicit) {
  if (explicit != null && explicit.trim() !== '') return explicit.trim().toLowerCase()
  const d = display.trim().toLowerCase()
  return G2P[d] || d
}
// A sound cell is "letters" or "letters|phonemecode".
function splitSound(raw) {
  const parts = String(raw).split('|')
  const text = parts[0].trim()
  return { text, code: codeFor(text, parts[1]) }
}

function fromSheet(w) {
  return {
    id: w.id,
    sounds: w.sounds.map((s) => {
      const t = splitSound(s.text)
      return { text: t.text, code: t.code, audio: s.audio || null }
    }),
    wordAudio: w.wordAudio || null,
  }
}
function fromBundled(w) {
  return {
    id: w.id,
    sounds: w.letters.map((l) => ({ text: l.grapheme, code: l.phoneme, audio: null })),
    wordAudio: null,
  }
}

// Ordered remote candidates for a phoneme code / a whole word (online first,
// then the local folder). audio.js tries each, then the bundled fallback.
function makeResolvers(driveIndex) {
  const phoneme = (code) => {
    const c = String(code).toLowerCase()
    const out = []
    if (driveIndex && driveIndex[c]) out.push(driveIndex[c])
    if (AUDIO_BASE_URL) out.push(joinUrl(AUDIO_BASE_URL, `${c}.mp3`))
    // Local folder: accept the configured ext plus common audio types so any drop works.
    if (PHONEME_BASE) for (const ext of LOCAL_EXTS) out.push(joinUrl(PHONEME_BASE, `${c}.${ext}`))
    return [...new Set(out)]
  }
  // Trimmed (silence-removed) phoneme, used for the blended word — prefer the
  // trimmed/ folder, then fall back to the normal phoneme sources.
  const phonemeTrimmed = (code) => {
    const c = String(code).toLowerCase()
    const out = []
    if (PHONEME_BASE) for (const ext of LOCAL_EXTS) out.push(joinUrl(PHONEME_BASE, `trimmed/${c}.${ext}`))
    return [...new Set([...out, ...phoneme(c)])]
  }
  const word = (id) => {
    const out = []
    if (driveIndex && driveIndex[id]) out.push(driveIndex[id])
    if (AUDIO_BASE_URL) out.push(joinUrl(AUDIO_BASE_URL, `${id}.mp3`))
    if (PHONEME_BASE) for (const ext of LOCAL_EXTS) out.push(joinUrl(PHONEME_BASE, `words/${id}.${ext}`))
    return [...new Set(out)]
  }
  return { phoneme, phonemeTrimmed, word }
}

function enrich(w, manifest, R) {
  const sounds = w.sounds.map((s) => {
    // Shared across words by code (so a ph:<code> recording overrides tap AND
    // blend). An explicit per-sound audio URL from the sheet is just the first
    // standard candidate for that code.
    const key = `ph:${s.code}`
    const remote = s.audio ? [s.audio, ...R.phoneme(s.code)] : R.phoneme(s.code)
    manifest[key] = { remote, bundled: bundledPh(s.code) }
    // Separate key for the blended word — uses the trimmed clips.
    const blendKey = `tph:${s.code}`
    manifest[blendKey] = { remote: R.phonemeTrimmed(s.code), bundled: bundledPh(s.code) }
    return { text: s.text, code: s.code, audioKey: key, blendKey }
  })
  const wordKey = `word:${w.id}`
  const wr = w.wordAudio ? [w.wordAudio, ...R.word(w.id)] : R.word(w.id)
  manifest[wordKey] = { remote: wr, bundled: bundledWord(w.id) }
  return { id: w.id, sounds, wordAudioKey: wordKey }
}

// Build one word on the fly (e.g. from the decoder's output) — returns the
// enriched word plus the audio manifest for its codes, so the caller can load it.
export function buildCustomWord(id, sounds) {
  const R = makeResolvers(null)
  const manifest = {}
  const word = enrich({ id: String(id || 'word').toLowerCase(), sounds: sounds.map((s) => ({ text: s.text, code: s.code, audio: null })) }, manifest, R)
  return { word, manifest }
}

export async function loadContent() {
  let raw = null, source = 'bundled'
  try {
    const sheet = await loadSheetWords()
    if (sheet && sheet.length) { raw = sheet.map(fromSheet); source = 'sheet' }
  } catch (_) { /* fall back to bundled */ }
  if (!raw) raw = BUNDLED.map(fromBundled)

  let driveIndex = null
  try { driveIndex = await loadDriveIndex() } catch (_) {}
  const R = makeResolvers(driveIndex)

  const manifest = {}
  const words = raw.map((w) => enrich(w, manifest, R))
  return { words, manifest, source }
}
